import { Checkbox, Divider } from "antd";
import React, { useCallback, useMemo } from "react";
import Icon, { ICON_NAME } from "../../../../../components/Icon";
import Menu from "../../../../../components/Menu";
import Note from "../../../../../components/Popup/PopupOpInfo/_component/Note";
import { datetime } from "../../../../../util/library/datetime";
import { ACTION_TYPE, KeyOfStep } from "../../../../../util/varibles/constants";
import { cloneObj } from "../../../../../util/varibles/global";
import { IOperation, IUnit } from "../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../util/varibles/message";
import { getKeyUnit, getLastPlaceByTime } from "../../../util";
import AddChecklist from "../_components/AddChecklist";
import AddNote from "../_components/AddNote";
import Certain from "../_components/Certain";
import ErrorOfOperation, { ErrorName } from "../_components/ErrorOfOperation";
import Factory from "../_components/Factory";
import Invoices from "../_components/Invoices";
import MortalityRisk from "../_components/MortalityRisk";
import SelectRoute from "../_components/SelectRoute";
import { getEditOp, useEditOp } from "../EditOperationContext";
import { EditSubOpProvider } from "../EditOperationContext/EditSubOperation";
import { getDeliveryTypes } from "./constants";
import DeliveryTypes from "./DeliveryTypes";
import DragOperation from "./DragOperation";
import styles from "./style.module.scss";
import UpdateActivityLog from "./UpdateActivityLog";

interface IProps {
    data: IOperation;
    startTime?: number
    startPlaceId?: string
    startPlaceField?: string

    onChange(data: Partial<IOperation>, isCall?: boolean): void;

    onDelete(): void
}

const Operation: React.FC<IProps> = React.memo(({
    data,
    startTime,
    startPlaceId,
    startPlaceField,
    onChange,
    onDelete
}) => {
    const { operation } = data;
    const { id, sub_operations, factory_id, note, certain_mode, error_detail } = operation;
    const get = getEditOp();

    const deliveryTypes = useMemo(() => {
        const { factories, vessel } = get();
        const factory = factories.find(item => item.id === factory_id);
        return (factory && vessel) ? getDeliveryTypes(factory, vessel) : {};
    }, [get, factory_id]);

    const handleUnitChange = useCallback((index: number, value: Partial<IUnit>, isCalculate?: boolean) => {
        const newUnits: IUnit[] = cloneObj(sub_operations);
        const error_detail = cloneObj(operation.error_detail || {});

        newUnits[index] = { ...newUnits[index], ...value };
        if (isCalculate) {
            let isFishAmountEmpty = false;
            const deliveryTypes = newUnits[index].delivery_types || [];
            if (deliveryTypes.length === 0) {
                const { site_name, unit_id } = newUnits[index];
                error_detail[ErrorName.DeliveryTypes] = {
                    ...error_detail[ErrorName.DeliveryTypes] || {},
                    [getKeyUnit(newUnits[index])]: {
                        message: notify[NotifyCode.E44]([`${site_name}, ${unit_id}`])
                    }
                }
                newUnits[index].fish_amount = 0;
                newUnits[index].total_weight = 0;
            } else {
                delete error_detail[ErrorName.DeliveryTypes];
                const { avg_weight = 1 } = newUnits[index] || {};
                const requiresFishAmount = avg_weight !== -1;
                const subTotal = deliveryTypes.reduce((rs, sub) => {
                    rs.fish_amount += sub.fish_amount || 0;
                    rs.total_weight += sub.total_weight;
                    isFishAmountEmpty = isFishAmountEmpty || (!sub.fish_amount && requiresFishAmount) || !sub.total_weight;
                    return rs
                }, { fish_amount: 0, total_weight: 0 });
                newUnits[index].fish_amount = subTotal.fish_amount;
                newUnits[index].total_weight = subTotal.total_weight;
                if (!isFishAmountEmpty) {
                    delete error_detail[ErrorName.FishAmount]
                } else {
                    error_detail[ErrorName.FishAmount] = { message: notify[NotifyCode.E37]([requiresFishAmount ? 'Fish amount, total weight' : 'Total weight']) }
                }
            }

            onChange({
                operation: {
                    ...operation,
                    ...newUnits.reduce((rs, item) => {
                        rs.total_weight += item.total_weight;
                        rs.total_amount += item.fish_amount || 0;
                        rs.fish_amount += item.fish_amount || 0;
                        return rs;
                    }, { total_weight: 0, total_amount: 0, fish_amount: 0 }),
                    sub_operations: newUnits,
                    error_detail
                }
            }, true)
        } else {
            delete error_detail[ErrorName.DeliveryTypes]
            onChange({ operation: { ...operation, sub_operations: newUnits, error_detail } })
        }
    }, [sub_operations, operation, onChange]);

    const handleDeleteUnit = useCallback((i: number) => {
        const newUnits: IUnit[] = cloneObj(sub_operations);
        newUnits.splice(i, 1);
        if (newUnits.length === 0) {
            onDelete()
        } else
            onChange({ operation: { ...operation, sub_operations: newUnits } }, true)
    }, [sub_operations, operation, onDelete, onChange]);

    return <Menu
        items={[{
            key: "delete",
            onClick: onDelete,
            label: <div className='menu-line' data-danger='true'>
                <Icon icon={ICON_NAME.DELETE} danger />
                <div>Delete trip</div>
            </div>
        }]}
        trigger={['contextMenu']}
    >
        <div id={id} className={styles.op}>
            <UpdateActivityLog
                data={data}
                startTime={startTime}
                startPlaceId={startPlaceId}
                startPlaceField={startPlaceField}
                onLoading={(loading, isCall) => onChange({ loading }, isCall)}
                onChange={(op = operation, activity_log = data.activity_log) => {
                    onChange({
                        operation: { ...operation, ...op },
                        activity_log,
                        loading: false
                    }, false);
                }}
            />
            <div className="flex justify-between items-center h-[42px]">
                <div className="flex items-center">
                    <DragOperation data={operation} />
                    <Divider className={styles.divider} orientation="vertical" />
                    <Factory
                        data={operation}
                        onChange={value => onChange({ operation: { ...operation, ...value, router_id: undefined } })}
                    />
                    <div className="ml-2">
                        <SelectRoute
                            startPlaceId={operation.site_id}
                            endPlaceId={operation.factory_id}
                            routerId={operation.router_id}
                            onChange={value => onChange({ operation: { ...operation, ...value } })}
                        />
                    </div>
                </div>
                <div className="flex items-center">
                    <AddNote
                        isLabel={false}
                        value={note}
                        title={`#${operation.operation_code}`}
                        onChange={value => onChange({ operation: { ...operation, note: value } })}
                    />
                    <Divider className={styles.divider} orientation="vertical" />
                    <Certain
                        value={certain_mode}
                        onChange={value => onChange({ operation: { ...operation, certain_mode: value } })}
                    />
                </div>
            </div>
            {(note || '').length > 0 && <Note {...{ isEdit: true, note }} handleUpdate={value => onChange({ operation: { ...operation, note: value } })} />}
            <div>
                <div className={styles.line}>
                    <div className="text-title">Site, unit</div>
                    <div className="text-title">Mortality</div>
                    <div className={styles.subLine}>
                        <div className="text-title">Delivery methods</div>
                        <div className="text-title">Vessel tanks</div>
                    </div>
                </div>
                {sub_operations.map((item, i) => <div key={item.id + item.harvest_id} className={styles.line}>
                    <EditSubOpProvider op={operation} vessel={get().vessel} unit={item} deliveryTypes={deliveryTypes}>
                        <div>{item.site_name || operation.site_name}, {item.unit_id}</div>
                        <div>
                            <MortalityRisk
                                value={item.mortality_risk}
                                onChange={value => handleUnitChange(i, { mortality_risk: value })}
                            />
                        </div>
                        <DeliveryTypes
                            data={item.delivery_types || []}
                            onChange={(value, isCalculate) => handleUnitChange(i, value, isCalculate)}
                        />
                        <div className={styles.menu}>
                            <Menu
                                placement="bottomRight"
                                items={[{
                                    key: "delete",
                                    onClick: () => handleDeleteUnit(i),
                                    label: <div className='menu-line' data-danger={true}>
                                        <Icon icon={ICON_NAME.DELETE} danger />
                                        <div>Delete unit</div>
                                    </div>
                                }]}
                            />
                        </div>
                    </EditSubOpProvider>
                </div>)}
            </div>
            <ErrorOfOperation error_detail={error_detail} />
            <div className='flex justify-end items-center mt-2'>
                <AddChecklist
                    data={operation}
                    onChange={value => onChange({ operation: { ...operation, checklist_ids: value } })}
                />
                <Checkbox
                    className='ml-3 whitespace-nowrap text-[15px]'
                    checked={operation.is_journey_to_site}
                    onChange={({ target }) => onChange({
                        operation: { ...operation, is_journey_to_site: target.checked }
                    })}
                >
                    Traveling to site step
                </Checkbox>
                <Checkbox
                    className='whitespace-nowrap text-[15px]'
                    checked={operation.is_cleaning}
                    onChange={({ target }) => onChange({ operation: { ...operation, is_cleaning: target.checked } })}
                >
                    Cleaning step
                </Checkbox>
            </div>
            <div className="mt-[6px] ml-[6px]">
                {get().editMode === ACTION_TYPE.EDIT && <Invoices
                    operation={operation}
                    activityLog={data.activity_log}
                    operations={get().operations}
                    vesselId={data.vessel.id}
                    onChange={invoices => onChange({ operation: { ...operation, invoices } })}
                />}
            </div>
        </div>
    </Menu>
}, (prev, next) => {
    return prev.data === next.data
        && prev.startTime === next.startTime
        && prev.startPlaceId === next.startPlaceId;
});

const Operations: React.FC = () => {
    const [ops, set] = useEditOp(state => state.harvests);
    const [startTime] = useEditOp(state => state.startTime);
    const get = getEditOp();
    const { hour, minute } = datetime(startTime);
    const startHourMin = hour * 60 + minute;
    const { id: opId } = (ops[0])?.operation || {};

    const { start_place_id, start_place_field } = useMemo(() => {
        const { id: vessel_id = '' } = get().vessel || {};
        return getLastPlaceByTime(vessel_id, startTime, [opId]);
    }, [get, startTime, opId])

    const handleUpdate = useCallback((index: number, data: Partial<IOperation>, isCall?: boolean) => {
        set(({ harvests: old }) => {
            const newOps = cloneObj(old);
            if (!newOps[index])
                return { harvests: old };

            newOps[index] = {
                ...newOps[index], ...data,
                isCall: isCall === undefined ? newOps[index].isCall : isCall
            };
            return { harvests: newOps };
        });
    }, [set]);

    const handleDelete = useCallback((index: number) => {
        set(({ harvests: old }) => {
            const newOps = cloneObj(old);
            newOps.splice(index, 1)
            return { harvests: newOps };
        });
    }, [set])

    return <div className="px-[20px] mt-2 flex flex-col gap-[24px]">
        {ops.map((item, i) => {
            let start, startPlaceId, startPlaceField;
            if (i) {
                const { activity_log = [], operation: { factory_id } } = ops[i - 1] || {};
                const { key } = activity_log[activity_log.length - 1] || {};
                if (key === KeyOfStep.COMPLETED_HARVEST) {
                    let currentDate = datetime(activity_log[activity_log.length - 1].est_start_time);
                    const { hour: currentHour, minute: currentMinute } = currentDate
                    const currentHourMin = currentHour * 60 + currentMinute;
                    if (currentHourMin > startHourMin) {
                        currentDate = currentDate.add(1, 'day')
                    }
                    currentDate = currentDate.startOf('hour').set({ hour, minute });
                    start = currentDate.time;
                }
                startPlaceId = factory_id
                startPlaceField = 'factory'
            } else {
                start = startTime;
                startPlaceId = start_place_id;
                startPlaceField = start_place_field;
            }

            return <Operation
                key={item?.operation?.id}
                data={item}
                startTime={start}
                startPlaceId={startPlaceId}
                startPlaceField={startPlaceField}
                onChange={(value, isCall) => handleUpdate(i, value, isCall)}
                onDelete={() => handleDelete(i)}
            />
        })}
    </div>;
};

export default Operations;
